import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import {
  AlertTriangle,
  TrendingUp,
  Users,
  MousePointer,
  Search,
  Filter,
  ExternalLink,
  ChevronRight,
  Activity
} from 'lucide-react';

interface Page {
  id: string;
  url: string;
  title: string;
  normalized_url: string;
  screenshot_url?: string;
  page_role?: string;
  total_sessions: number;
  friction_sessions: number;
  avg_friction_severity?: number;
  friction_types: number;
  friction_rate: string;
  improvement_potential: number;
  status: 'healthy' | 'warning' | 'critical';
  last_activity?: string;
}

interface PagePickerProps {
  siteId: string;
  onPageSelect: (page: Page) => void;
  selectedPage?: Page | null;
}

export default function PagePicker({ siteId, onPageSelect, selectedPage }: PagePickerProps) {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('friction');

  useEffect(() => {
    if (siteId) {
      fetchPages();
    }
  }, [siteId]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `${API_URL}/growth/pages/${siteId}`
      );
      const data = await response.json();
      setPages(data.pages || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPages = pages.filter(page => {
    // Search filter
    if (searchQuery && !page.url.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !page.title?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Role filter
    if (filterRole !== 'all' && page.page_role !== filterRole) {
      return false;
    }

    // Status filter
    if (filterStatus !== 'all' && page.status !== filterStatus) {
      return false;
    }

    return true;
  });

  const sortedPages = [...filteredPages].sort((a, b) => {
    switch (sortBy) {
      case 'friction':
        return b.friction_sessions - a.friction_sessions;
      case 'traffic':
        return b.total_sessions - a.total_sessions;
      case 'potential':
        return b.improvement_potential - a.improvement_potential;
      case 'severity':
        return (b.avg_friction_severity || 0) - (a.avg_friction_severity || 0);
      default:
        return 0;
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'bg-red-100 border-red-300 text-red-800';
      case 'warning':
        return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'healthy':
        return 'bg-green-100 border-green-300 text-green-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role?: string) => {
    const colors: Record<string, string> = {
      home: 'bg-blue-500',
      product_detail: 'bg-purple-500',
      category: 'bg-green-500',
      cart: 'bg-orange-500',
      checkout: 'bg-red-500',
      search: 'bg-indigo-500'
    };
    return colors[role || ''] || 'bg-gray-500';
  };

  const formatUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search pages by URL or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Page Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="home">Home</SelectItem>
            <SelectItem value="product_detail">Product</SelectItem>
            <SelectItem value="category">Category</SelectItem>
            <SelectItem value="cart">Cart</SelectItem>
            <SelectItem value="checkout">Checkout</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="healthy">Healthy</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="friction">Friction Count</SelectItem>
            <SelectItem value="traffic">Traffic</SelectItem>
            <SelectItem value="potential">Potential</SelectItem>
            <SelectItem value="severity">Severity</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{pages.length}</div>
            <p className="text-sm text-muted-foreground">Total Pages</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">
              {pages.filter(p => p.status === 'critical').length}
            </div>
            <p className="text-sm text-muted-foreground">Critical</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {pages.filter(p => p.friction_sessions > 0).length}
            </div>
            <p className="text-sm text-muted-foreground">With Friction</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {pages.filter(p => p.status === 'healthy').length}
            </div>
            <p className="text-sm text-muted-foreground">Healthy</p>
          </CardContent>
        </Card>
      </div>

      {/* Pages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedPages.map(page => (
          <Card
            key={page.id}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              selectedPage?.id === page.id ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => onPageSelect(page)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-sm font-medium">
                    {formatUrl(page.url)}
                  </CardTitle>
                  {page.title && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {page.title}
                    </p>
                  )}
                </div>
                <Badge className={getStatusColor(page.status)} variant="secondary">
                  {page.status}
                </Badge>
              </div>
            </CardHeader>

            <CardContent>
              {/* Screenshot Thumbnail */}
              {page.screenshot_url && (
                <div className="mb-3 rounded-lg overflow-hidden bg-gray-100 h-32">
                  <img
                    src={page.screenshot_url}
                    alt={page.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              )}

              {/* Page Role */}
              {page.page_role && (
                <Badge
                  className={`${getRoleBadgeColor(page.page_role)} text-white mb-2`}
                  variant="secondary"
                >
                  {page.page_role.replace('_', ' ')}
                </Badge>
              )}

              {/* Metrics */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center text-muted-foreground">
                    <Users className="h-3 w-3 mr-1" />
                    Sessions
                  </span>
                  <span className="font-medium">{page.total_sessions}</span>
                </div>

                {page.friction_sessions > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-orange-600">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Friction
                    </span>
                    <span className="font-medium text-orange-600">
                      {page.friction_sessions} ({page.friction_rate}%)
                    </span>
                  </div>
                )}

                {page.friction_types > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-muted-foreground">
                      <Activity className="h-3 w-3 mr-1" />
                      Issues
                    </span>
                    <span className="font-medium">
                      {page.friction_types} types
                    </span>
                  </div>
                )}

                {page.improvement_potential > 50 && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-green-600">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      Potential
                    </span>
                    <span className="font-medium text-green-600">
                      High
                    </span>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onPageSelect(page);
                }}
              >
                Analyze Page
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {sortedPages.length === 0 && !loading && (
        <Card className="p-8">
          <CardContent className="text-center">
            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery || filterRole !== 'all' || filterStatus !== 'all'
                ? 'No pages match your filters'
                : 'No pages found for this site'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}